// customRenderRegistry — runtime registry for wizard step components that
// go beyond the canonical field-primitive set. Mirrors the v1 SpecWizard
// MIGRATION_PLAYBOOK pattern (web/dashboard/src/lib/wizard-components/registry.ts).
//
// Manifest fields declare `props.rendered_by_component: <id>` to opt into
// custom rendering. The corresponding ID must be registered before the
// wizard mounts; unregistered IDs render a "not registered" stub so failures
// are loud, not silent.
//
// CHG-1247 registers 2 forward-promised stubs from CHG-1241:
//   ImportColumnMappingTable (import-map step "map")
//   ImportPreviewTable        (import-map step "preview")
// Real components ship in a future CHG.

import type { ComponentType, ReactNode } from "react";

export interface CustomRenderProps {
  /** Field name from the manifest — passed straight through from FormDialog. */
  name: string;
  /** Current value managed by FormDialog's parent. */
  value: unknown;
  /** Setter from FormDialog. */
  onChange: (value: unknown) => void;
  /** Label declared on the manifest field. */
  label?: string;
  /** All other manifest props (rendered_by_component, opaque_json, etc.). */
  [key: string]: unknown;
}

export type CustomRenderComponent = ComponentType<CustomRenderProps>;

const registry = new Map<string, CustomRenderComponent>();

export function registerWizardComponent(id: string, component: CustomRenderComponent): void {
  if (!id || typeof id !== "string") {
    throw new Error("registerWizardComponent: id must be a non-empty string");
  }
  if (typeof component !== "function") {
    throw new Error(`registerWizardComponent: component for '${id}' must be a React component`);
  }
  registry.set(id, component);
}

export function getWizardComponent(id: string): CustomRenderComponent | undefined {
  return registry.get(id);
}

/** Test helper — clears all registrations. Not exported for prod consumers. */
export function _resetRegistryForTests(): void {
  registry.clear();
}

/** Fallback rendered when an id isn't registered. Loud + stable selectors. */
export function unregisteredStub(id: string): ReactNode {
  return (
    <div
      data-wizard-stub="true"
      data-wizard-component-id={id}
      role="alert"
      style={{
        padding: "12px 16px",
        borderRadius: "var(--radius-md)",
        background: "var(--color-warning-tint)",
        color: "var(--color-warning)",
        fontSize: 12,
        border: "1px dashed var(--color-warning)",
      }}
    >
      Component <code>{id}</code> is not registered. Register it via{" "}
      <code>registerWizardComponent</code> before mounting this wizard.
    </div>
  );
}
